import { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { buildKbCss, fetchKbSettings, setKbSettingsCache } from "@/lib/kbSettings";
import AppUnavailable from "@/pages/AppUnavailable";
import KbCalloutStylesCard from "@/pages/settings/KbCalloutStylesCard";

const FONTS = ["Manrope", "IBM Plex Sans", "Inter", "Roboto", "Work Sans", "Nunito Sans", "Lora", "Merriweather", "Georgia", "Courier New"];

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-400 dark:text-slate-500">{hint}</p>}
    </div>
  );
}
function ColorRow({ label, value, onChange, disabled }) {
  return (
    <div className={`flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2 ${disabled ? "opacity-50" : ""}`}>
      <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono text-xs text-slate-400 dark:text-slate-500">{disabled ? "brand" : (value || "#2563eb")}</span>
        <input type="color" value={value || "#2563eb"} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-10 cursor-pointer rounded border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" />
      </span>
    </div>
  );
}

export default function KBSettings() {
  const [form, setForm] = useState(null);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/kb/settings").then((r) => {
      if (!r.data.can_edit) { setDenied(true); return; }
      setForm(r.data);
    }).catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);

  // Live preview: push the working stylesheet into the global KB style tag.
  useEffect(() => {
    if (!form) return;
    const id = "kb-dynamic-style";
    let el = document.getElementById(id);
    if (!el) { el = document.createElement("style"); el.id = id; document.head.appendChild(el); }
    el.textContent = buildKbCss(form);
  }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/kb/settings", form);
      setForm(data);
      setKbSettingsCache(data);
      await fetchKbSettings(true);
      toast.success("KB styles saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (denied) return <AppUnavailable appName="KB Branding" inactive={false} />;
  if (!form) return <div className="flex h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>;

  return (
    <div className="kb-surface" data-testid="kb-settings-page">
      <PageHeader compact title="Branding" testid="kb-settings-header"
        subtitle="Fonts, colours, tables and callout styles for every Knowledge Base article">
        <Button onClick={save} disabled={saving} data-testid="kb-settings-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Body font">
              <select data-testid="kb-set-body-font" value={form.body_font} onChange={(e) => set("body_font", e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 dark:border-slate-800 px-2 text-sm">
                {FONTS.map((f) => <option key={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Heading font">
              <select data-testid="kb-set-heading-font" value={form.heading_font} onChange={(e) => set("heading_font", e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 dark:border-slate-800 px-2 text-sm">
                {FONTS.map((f) => <option key={f}>{f}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label={`Text size (${form.base_font_size}px)`}>
              <input type="range" min="11" max="24" value={form.base_font_size} data-testid="kb-set-font-size"
                onChange={(e) => set("base_font_size", Number(e.target.value))} className="w-full" />
            </Field>
            <Field label={`Line height (${form.line_height})`}>
              <input type="range" min="1.2" max="2.4" step="0.05" value={form.line_height} data-testid="kb-set-line-height"
                onChange={(e) => set("line_height", Number(e.target.value))} className="w-full" />
            </Field>
            <Field label="Content width (px)" hint="0 = full width">
              <Input type="number" value={form.content_width} data-testid="kb-set-width"
                onChange={(e) => set("content_width", Number(e.target.value))} className="h-9" />
            </Field>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Use partner brand colours</div>
              <div className="text-xs text-slate-400 dark:text-slate-500">Links &amp; accents follow each partner's brand when they view the KB.</div>
            </div>
            <Switch checked={!!form.use_brand_colors} data-testid="kb-set-brand-colors"
              onCheckedChange={(v) => set("use_brand_colors", v)} />
          </div>

          <div className="space-y-2">
            <ColorRow label="Body text" value={form.text_color} onChange={(v) => set("text_color", v)} />
            <ColorRow label="Headings" value={form.heading_color} onChange={(v) => set("heading_color", v)} />
            <ColorRow label="Links" value={form.link_color} onChange={(v) => set("link_color", v)} disabled={form.use_brand_colors} />
            <ColorRow label="Table header" value={form.table_header_bg} onChange={(v) => set("table_header_bg", v)} />
            <ColorRow label="Table borders" value={form.table_border_color} onChange={(v) => set("table_border_color", v)} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2">
            <span className="text-sm text-slate-600 dark:text-slate-300">Striped table rows</span>
            <Switch checked={!!form.table_stripe} data-testid="kb-set-stripe" onCheckedChange={(v) => set("table_stripe", v)} />
          </div>
          {form.is_super_admin && (
            <Field label="Embed hosts"
              hint="Only these hosts can be embedded as iframes in articles. One per line — the built-in list (YouTube, Vimeo, Loom, Synthesia, Scribe…) always applies.">
              <textarea value={(form.extra_embed_hosts || []).join("\n")} data-testid="kb-set-embed-hosts"
                placeholder="app.example.com"
                onChange={(e) => set("extra_embed_hosts", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                className="min-h-[80px] w-full rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2 font-mono text-xs" />
              <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                Currently allowed: {(form.embed_hosts || []).join(", ")}
              </p>
            </Field>
          )}
          <Field label="Custom CSS" hint="Advanced — targets .kb-prose. Applied after the settings above.">
            <textarea value={form.custom_css || ""} data-testid="kb-set-custom-css"
              onChange={(e) => set("custom_css", e.target.value)}
              className="min-h-[120px] w-full rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2 font-mono text-xs" />
          </Field>
        </div>

        <div>
          <div className="sticky top-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Live preview</span>
              <RotateCcw className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" />
            </div>
            <div id="kb-article-body" className="kb-prose" data-testid="kb-settings-preview">
              <h1>Ordering Snom</h1>
              <p>When you need to order Snom items, we issue a PO out of QuickBooks. This is a sample of how article <a href="#">links</a>, <strong>bold text</strong>, and tables look with the current settings.</p>
              <h2>Pricing</h2>
              <ul><li>Check the current price list</li><li>Confirm ship-to address</li></ul>
              <table><thead><tr><th>Item</th><th>Price</th></tr></thead>
                <tbody><tr><td>Snom D717</td><td>$129</td></tr><tr><td>Snom D735</td><td>$189</td></tr></tbody></table>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6"><KbCalloutStylesCard /></div>
    </div>
  );
}
